import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import PinInput from "./PinInput";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const ChangePassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading, changePassword } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirm) {
      toast({ title: "PINs do not match", description: "Please enter the same 6 digits twice." });
      return;
    }
    if (!/^\d{6}$/.test(newPassword)) {
      toast({ title: "PIN must be 6 digits", description: "Use exactly 6 numbers." });
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(newPassword, confirm);
      toast({ title: "PIN updated", description: "Your new PIN is set." });
      navigate("/feed");
    } catch (err) {
      toast({ title: "Could not update", description: err?.response?.data?.detail || "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Set a new PIN"
      subtitle="You logged in with a temporary PIN. Choose your own 6-digit PIN to continue."
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="new">New 6-digit PIN</Label>
          <PinInput id="new" required value={newPassword} onChange={setNewPassword} placeholder="••••••" data-testid="change-pin-new" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm PIN</Label>
          <PinInput id="confirm" required value={confirm} onChange={setConfirm} placeholder="••••••" data-testid="change-pin-confirm" />
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="h-12 w-full rounded-full bg-[#FF4500] text-base font-bold text-white hover:bg-[#e03d00]"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update PIN"}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ChangePassword;
